import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select, TextField, type SelectOption } from '@chatsdet/ui';
import type { EnvironmentStage } from '@chatsdet/types';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';

const CUSTOM_KEY = '__custom__';
const UNASSIGNED_KEY = '__unassigned__';

type EnvironmentStageResponse = {
  stages: EnvironmentStage[];
};

export type EnvironmentStageSelection = {
  id: string | null;
  name: string;
};

type Props = {
  stageId: string | null;
  stageName?: string;
  onChange: (stage: EnvironmentStageSelection) => void;
  allowUnassigned?: boolean;
};

export function EnvironmentStageSelect({
  stageId,
  stageName = '',
  onChange,
  allowUnassigned = false,
}: Props) {
  const stagesQ = useQuery({
    queryKey: ['environment-kinds'],
    queryFn: async (): Promise<EnvironmentStageResponse> => {
      const response = await fetch(`${API_URL}/org/environment-kinds`, {
        credentials: 'include',
      });
      if (!response.ok) throw await toApiError(response, 'Failed to fetch stages');
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const stages = stagesQ.data?.stages ?? [];
  const matchingStage = stageId
    ? stages.find((stage) => stage.id === stageId)
    : stages.find((stage) => stage.name === stageName);
  const isCustom = !stageId && stageName !== '' && !matchingStage;
  const [customName, setCustomName] = React.useState(isCustom ? stageName : '');
  const [customSelected, setCustomSelected] = React.useState(isCustom);

  React.useEffect(() => {
    if (matchingStage) {
      setCustomSelected(false);
      return;
    }
    if (isCustom) {
      setCustomName(stageName);
      setCustomSelected(true);
    }
  }, [isCustom, matchingStage, stageName]);

  const items: SelectOption[] = [
    ...(allowUnassigned ? [{ id: UNASSIGNED_KEY, label: 'No stage' }] : []),
    ...stages.map((stage) => ({ id: stage.id, label: capitalize(stage.name) })),
    { id: CUSTOM_KEY, label: 'Custom…' },
  ];
  const selectedKey =
    matchingStage?.id ?? (customSelected ? CUSTOM_KEY : allowUnassigned ? UNASSIGNED_KEY : null);

  return (
    <div className="space-y-3">
      <Select
        label="Stage"
        description="Match this environment with the same stage in other applications."
        items={items}
        selectedKey={selectedKey}
        placeholder={stagesQ.isLoading ? 'Loading stages…' : 'Select a stage…'}
        onSelectionChange={(key) => {
          const selected = String(key);
          if (selected === CUSTOM_KEY) {
            setCustomSelected(true);
            onChange({ id: null, name: customName });
            return;
          }
          if (selected === UNASSIGNED_KEY) {
            setCustomSelected(false);
            onChange({ id: null, name: '' });
            return;
          }
          const stage = stages.find((candidate) => candidate.id === selected);
          if (stage) {
            setCustomSelected(false);
            onChange({ id: stage.id, name: stage.name });
          }
        }}
      />

      {customSelected && (
        <TextField
          label="Custom stage name"
          value={customName}
          onChange={(name) => {
            setCustomName(name);
            onChange({ id: null, name });
          }}
          placeholder="e.g. qa-eu, sandbox, demo"
          isRequired
        />
      )}
    </div>
  );
}

function capitalize(value: string): string {
  return value.length === 0 ? value : value[0].toUpperCase() + value.slice(1);
}
